import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { adminAPI } from '../../utils/api';
import { Card } from '../../components/ui/card';
import {
  Users, FileCheck, TrendingUp, ArrowDownLeft, ArrowUpRight, MessageSquare,
  BookOpen, ShieldCheck, Wallet, PlusCircle, ArrowLeftRight, Activity, ChevronRight,
} from 'lucide-react';

const COLOR = {
  blue:   { icon: 'text-blue-600 dark:text-blue-400',   iconBg: 'bg-blue-100 dark:bg-blue-900/30',   badge: 'border-blue-400 text-blue-600 dark:text-blue-400',   btn: 'text-blue-600 dark:text-blue-400'   },
  teal:   { icon: 'text-teal-600 dark:text-teal-400',   iconBg: 'bg-teal-100 dark:bg-teal-900/30',   badge: 'border-teal-400 text-teal-600 dark:text-teal-400',   btn: 'text-teal-600 dark:text-teal-400'   },
  green:  { icon: 'text-green-600 dark:text-green-400',  iconBg: 'bg-green-100 dark:bg-green-900/30',  badge: 'border-green-400 text-green-600 dark:text-green-400',  btn: 'text-green-600 dark:text-green-400'  },
  purple: { icon: 'text-purple-600 dark:text-purple-400', iconBg: 'bg-purple-100 dark:bg-purple-900/30', badge: 'border-purple-400 text-purple-600 dark:text-purple-400', btn: 'text-purple-600 dark:text-purple-400' },
  orange: { icon: 'text-orange-600 dark:text-orange-400', iconBg: 'bg-orange-100 dark:bg-orange-900/30', badge: 'border-orange-400 text-orange-600 dark:text-orange-400', btn: 'text-orange-600 dark:text-orange-400' },
};

const WORKFLOW_STEPS = [
  {
    num: 1, color: 'blue', Icon: ShieldCheck,
    title: 'Approve KYC',
    description: "Review and approve the user's identity documents",
    action: { label: 'Go to KYC →', path: '/admin/kyc' },
  },
  {
    num: 2, color: 'teal', Icon: Wallet,
    title: 'Assign Wallets',
    description: 'Assign blockchain wallet addresses for each asset the user needs',
    action: { label: 'Go to Users →', path: '/admin/users' },
    note: 'Click the Wallet button on the user row',
  },
  {
    num: 3, color: 'green', Icon: PlusCircle,
    title: 'Fund Account',
    description: 'Credit GBP or crypto balance via Balance Management',
    action: { label: 'Go to Users →', path: '/admin/users' },
    note: 'Open Wallets → Balance Management section',
  },
  {
    num: 4, color: 'purple', Icon: ArrowLeftRight,
    title: 'User Can Now Trade',
    description: 'User can buy crypto, transfer funds, and use mining',
  },
  {
    num: 5, color: 'orange', Icon: Activity,
    title: 'Monitor Activity',
    description: 'Review trades, deposits and withdrawals in the admin panel',
    action: { label: 'Audit Logs →', path: '/admin/audit-logs' },
  },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getDashboardStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="loading-spinner"></div></div>;

  const statCards = [
    { label: 'Total Users',           value: stats?.total_users,         icon: Users,         color: 'bg-blue-500/10 text-blue-400'    },
    { label: 'Active Users',          value: stats?.active_users,        icon: Users,         color: 'bg-green-500/10 text-green-400'  },
    { label: 'Pending KYC',           value: stats?.pending_kyc,         icon: FileCheck,     color: 'bg-yellow-500/10 text-yellow-400'},
    { label: 'Pending Trades',        value: stats?.pending_trades,      icon: TrendingUp,    color: 'bg-purple-500/10 text-purple-400'},
    { label: 'Pending Deposits',      value: stats?.pending_deposits,    icon: ArrowDownLeft, color: 'bg-emerald-500/10 text-emerald-400'},
    { label: 'Pending Withdrawals',   value: stats?.pending_withdrawals, icon: ArrowUpRight,  color: 'bg-red-500/10 text-red-400'      },
    { label: 'Open Tickets',          value: stats?.open_tickets,        icon: MessageSquare, color: 'bg-orange-500/10 text-orange-400'},
  ];

  const quickStats = [
    { label: 'Total Users',           value: stats?.total_users  ?? '—', bg: 'bg-blue-50 dark:bg-blue-900/20',   text: 'text-blue-700 dark:text-blue-400',   border: 'border-blue-200 dark:border-blue-800'   },
    { label: 'Pending KYC',           value: stats?.pending_kyc  ?? '—', bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
    { label: 'Awaiting Wallet Setup', value: '—',                         bg: 'bg-red-50 dark:bg-red-900/20',     text: 'text-red-600 dark:text-red-400',     border: 'border-red-200 dark:border-red-800'     },
    { label: 'Active Today',          value: '—',                         bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-800' },
  ];

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl sm:text-4xl font-bold text-foreground mb-2">Admin Dashboard</h1>
        <p className="text-muted-foreground">Overview of platform activity</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, idx) => (
          <Card key={idx} className="stat-card" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm mb-1">{stat.label}</p>
                <h3 className="text-3xl font-bold text-foreground">{stat.value || 0}</h3>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                <stat.icon className="w-6 h-6" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* New User Setup Workflow */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 shadow-sm">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
            <BookOpen size={18} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">New User Setup Workflow</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Complete these steps in order for every new user</p>
          </div>
        </div>

        {/* Steps */}
        <div className="flex items-stretch gap-2 flex-wrap sm:flex-nowrap">
          {WORKFLOW_STEPS.map((step, idx) => {
            const c = COLOR[step.color];
            return (
              <React.Fragment key={step.num}>
                <div className="flex-1 relative bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600 min-w-[140px]">
                  <span className={`absolute -top-2.5 -left-2.5 w-6 h-6 rounded-full bg-white dark:bg-gray-800 border-2 flex items-center justify-center text-xs font-bold ${c.badge}`}>
                    {step.num}
                  </span>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${c.iconBg}`}>
                    <step.Icon size={16} className={c.icon} />
                  </div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{step.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{step.description}</p>
                  {step.note && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 italic">{step.note}</p>
                  )}
                  {step.action && (
                    <button
                      onClick={() => navigate(step.action.path)}
                      className={`mt-3 text-xs font-medium hover:underline cursor-pointer flex items-center gap-1 ${c.btn}`}
                    >
                      {step.action.label}
                    </button>
                  )}
                </div>
                {idx < WORKFLOW_STEPS.length - 1 && (
                  <ChevronRight size={20} className="text-gray-300 dark:text-gray-600 flex-shrink-0 self-center hidden sm:block" />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Quick stats chips */}
        <div className="flex items-center gap-3 flex-wrap mt-6">
          {quickStats.map((qs) => (
            <div
              key={qs.label}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium ${qs.bg} ${qs.text} ${qs.border}`}
            >
              <span>{qs.label}:</span>
              <span className="font-bold">{qs.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
